import React, { useContext, useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CabinCrewLayout from '../layouts/CabinCrewLayout';
import { AuthContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { 
  ChevronLeft, AlertTriangle, Volume2, ChevronRight,
  CheckCircle, Loader2, Shield, Flame, Heart, Plane
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Emergency procedures
const EMERGENCY_PROCEDURES = [
  {
    id: 'evacuation',
    name: 'Tahliye Prosedürü',
    description: 'Acil tahliye adımları',
    icon: '🚨',
    color: 'from-red-500 to-orange-600',
    steps: [
      { command: "BRACE! BRACE! HEADS DOWN! STAY DOWN!", command_tr: "KORUMA POZİSYONU! BAŞLAR AŞAĞI! KALMAYIN!", audio: true },
      { command: "RELEASE SEATBELTS!", command_tr: "EMNİYET KEMERLERİNİ AÇIN!", audio: true },
      { command: "LEAVE EVERYTHING! COME THIS WAY!", command_tr: "HER ŞEYİ BIRAKIN! BU TARAFA GELİN!", audio: true },
      { command: "JUMP AND SLIDE! JUMP AND SLIDE!", command_tr: "ATLAYIN VE KAYIN! ATLAYIN VE KAYIN!", audio: true },
      { command: "RUN AWAY FROM THE AIRCRAFT!", command_tr: "UÇAKTAN UZAKLAŞIN!", audio: true }
    ]
  },
  {
    id: 'fire',
    name: 'Yangın Prosedürü',
    description: 'Kabin yangını müdahalesi',
    icon: '🔥',
    color: 'from-orange-500 to-red-600',
    steps: [
      { command: "FIRE! FIRE! FIRE IN THE CABIN!", command_tr: "YANGIN! YANGIN! KABİNDE YANGIN!", audio: true },
      { command: "LOCATE THE FIRE EXTINGUISHER!", command_tr: "YANGIN SÖNDÜRÜCÜYÜ BULUN!", audio: true },
      { command: "PULL THE PIN, AIM AT THE BASE!", command_tr: "PİMİ ÇEKİN, TABANA NİŞAN ALIN!", audio: true },
      { command: "SQUEEZE THE HANDLE!", command_tr: "KOLU SIKIN!", audio: true },
      { command: "SWEEP SIDE TO SIDE!", command_tr: "SAĞA SOLA SÜPÜRÜN!", audio: true }
    ]
  },
  {
    id: 'medical',
    name: 'Tıbbi Acil Durum',
    description: 'Sağlık acili müdahalesi',
    icon: '🏥',
    color: 'from-blue-500 to-cyan-600',
    steps: [
      { command: "IS THERE A DOCTOR ON BOARD?", command_tr: "UÇAKTA DOKTOR VAR MI?", audio: true },
      { command: "PLEASE REMAIN CALM!", command_tr: "LÜTFEN SAKİN KALIN!", audio: true },
      { command: "GET THE FIRST AID KIT!", command_tr: "İLK YARDIM ÇANTASINI GETİRİN!", audio: true },
      { command: "PREPARE THE OXYGEN!", command_tr: "OKSİJENİ HAZIRLAYIN!", audio: true },
      { command: "WE NEED TO MAKE AN EMERGENCY LANDING!", command_tr: "ACİL İNİŞ YAPMAMIZ GEREKİYOR!", audio: true }
    ]
  },
  {
    id: 'decompression',
    name: 'Basınç Kaybı',
    description: 'Kabin basınç kaybı prosedürü',
    icon: '💨',
    color: 'from-purple-500 to-indigo-600',
    steps: [
      { command: "OXYGEN! OXYGEN! PUT ON YOUR MASKS!", command_tr: "OKSİJEN! OKSİJEN! MASKELERİNİZİ TAKIN!", audio: true },
      { command: "PULL THE MASK TOWARDS YOU!", command_tr: "MASKEYİ KENDİNİZE DOĞRU ÇEKİN!", audio: true },
      { command: "PLACE IT OVER YOUR NOSE AND MOUTH!", command_tr: "BURNUNUZA VE AĞZINIZA YERLEŞTİRİN!", audio: true },
      { command: "BREATHE NORMALLY!", command_tr: "NORMAL NEFES ALIN!", audio: true },
      { command: "HELP OTHERS AFTER SECURING YOUR OWN MASK!", command_tr: "KENDİ MASKENİZİ TAKTIKTAN SONRA BAŞKALARINA YARDIM EDİN!", audio: true }
    ]
  }
];

export default function CabinCrewEmergency() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [selectedProcedure, setSelectedProcedure] = useState(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [practiceMode, setPracticeMode] = useState(false);
  
  const audioRef = useRef(null);

  // Demo kullanıcıları bu sayfaya erişemez
  useEffect(() => {
    if (user?.is_demo) {
      toast.error('Bu özellik tam sürümde mevcuttur');
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const currentProcedure = EMERGENCY_PROCEDURES.find(p => p.id === selectedProcedure);
  const currentStep = currentProcedure?.steps[currentStepIndex];

  const playAudio = async (text) => {
    if (!text) return;
    
    // First try browser TTS as primary (OpenAI rate limited)
    if ('speechSynthesis' in window) {
      try {
        setAudioPlaying(true);
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.lang = 'en-US';
        utterance.rate = 0.9;
        utterance.onend = () => setAudioPlaying(false);
        utterance.onerror = () => setAudioPlaying(false);
        speechSynthesis.speak(utterance);
        return;
      } catch (e) {
        console.error('Browser TTS failed:', e);
      }
    }
    
    // Fallback to API if browser TTS not available
    try {
      setAudioPlaying(true);
      const response = await axios.post(
        `${API_URL}/api/speaking/text-to-speech`,
        { text },
        { responseType: 'blob' }
      );
      const contentType = response.headers['content-type'] || '';
      if (contentType.includes('audio')) {
        const audioUrl = URL.createObjectURL(response.data);
        if (audioRef.current) {
          audioRef.current.src = audioUrl;
          audioRef.current.play();
        }
      }
    } catch (error) {
      console.error('Audio API error:', error);
    } finally {
      setAudioPlaying(false);
    }
  };

  const handleNext = () => {
    if (!completedSteps.includes(currentStepIndex)) {
      setCompletedSteps([...completedSteps, currentStepIndex]);
    }
    
    if (currentStepIndex < currentProcedure.steps.length - 1) {
      setCurrentStepIndex(currentStepIndex + 1);
    } else {
      toast.success('Prosedür tamamlandı! 🎉');
    }
  };

  // Procedure selection
  if (!selectedProcedure) {
    return (
      <CabinCrewLayout>
        <div className="min-h-screen bg-gradient-to-br from-red-50 via-orange-50 to-yellow-50 p-4">
          <audio ref={audioRef} onEnded={() => setAudioPlaying(false)} />
          
          <div className="container mx-auto max-w-4xl">
            <div className="flex items-center gap-4 mb-8">
              <Button variant="ghost" onClick={() => navigate('/dashboard')}>
                <ChevronLeft className="w-5 h-5 mr-1" />
                Ana Sayfa
              </Button>
              <div>
                <h1 className="text-3xl font-black text-gray-800 flex items-center gap-3">
                  <AlertTriangle className="w-8 h-8 text-red-500" />
                  Acil Durum Prosedürleri
                </h1>
                <p className="text-gray-600">Kritik komutları öğrenin ve pratik yapın</p>
              </div>
            </div>

            {/* Warning Card */}
            <Card className="mb-8 border-2 border-red-200 bg-red-50">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-red-500 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Shield className="w-6 h-6 text-white" />
                  </div>
                  <div>
                  <h3 className="text-lg font-bold text-red-800 mb-2">Önemli Uyarı</h3>
                  <p className="text-red-700 text-sm">
                    Bu komutları net, yüksek sesle ve güvenli bir şekilde söylemeniz gerekmektedir. 
                    Acil durumlarda panik yapmadan, prosedürleri adım adım uygulayın.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {EMERGENCY_PROCEDURES.map((procedure) => (
              <Card 
                key={procedure.id}
                className="cursor-pointer hover:shadow-xl transition-all hover:-translate-y-1 border-2 border-transparent hover:border-red-300"
                onClick={() => {
                  setSelectedProcedure(procedure.id);
                  setCurrentStepIndex(0);
                  setCompletedSteps([]);
                }}
              >
                <CardContent className="p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className={`w-16 h-16 bg-gradient-to-br ${procedure.color} rounded-xl flex items-center justify-center`}>
                      <span className="text-3xl">{procedure.icon}</span>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-800">{procedure.name}</h3>
                      <p className="text-sm text-gray-500">{procedure.description}</p>
                    </div>
                  </div>
                  <Badge className={`bg-gradient-to-r ${procedure.color} text-white`}>
                    {procedure.steps.length} Adım
                  </Badge>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
      </CabinCrewLayout>
    );
  }

  // Emergency practice screen
  return (
    <CabinCrewLayout>
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-orange-50 to-yellow-50 p-4">
        <audio ref={audioRef} onEnded={() => setAudioPlaying(false)} />
        
        <div className="container mx-auto max-w-2xl">
          <div className="flex items-center justify-between mb-6">
            <Button variant="ghost" onClick={() => setSelectedProcedure(null)}>
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <div className="text-center">
              <p className="text-sm text-gray-500">{currentProcedure?.name}</p>
              <p className="font-bold">Adım {currentStepIndex + 1} / {currentProcedure?.steps.length}</p>
            </div>
            <Badge className={`bg-gradient-to-r ${currentProcedure?.color} text-white`}>
              {currentProcedure?.icon}
          </Badge>
        </div>

        <Progress 
          value={((currentStepIndex + 1) / currentProcedure?.steps.length) * 100}
          className="h-3 mb-6"
        />

        {/* Command Card */}
        <Card className="border-4 border-red-300 shadow-2xl mb-6 overflow-hidden">
          <div className={`bg-gradient-to-r ${currentProcedure?.color} p-4`}>
            <div className="flex items-center justify-between text-white">
              <span className="font-bold">🚨 ACİL KOMUT</span>
              <button
                onClick={() => playAudio(currentStep?.command)}
                disabled={audioPlaying}
                className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30"
              >
                {audioPlaying ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Volume2 className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>
          <CardContent className="p-6">
            <p className="text-2xl font-black text-gray-800 text-center mb-4 leading-tight">
              {currentStep?.command}
            </p>
            <div className="bg-gray-100 rounded-xl p-4">
              <p className="text-center text-gray-600">
                {currentStep?.command_tr}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Step indicators */}
        <div className="flex justify-center gap-2 mb-6">
          {currentProcedure?.steps.map((_, idx) => (
            <div
              key={idx}
              className={`w-4 h-4 rounded-full transition-all ${
                completedSteps.includes(idx)
                  ? 'bg-green-500'
                  : idx === currentStepIndex
                  ? 'bg-red-500 scale-125'
                  : 'bg-gray-300'
              }`}
            />
          ))}
        </div>

        {/* Navigation */}
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={() => {
              if (currentStepIndex > 0) {
                setCurrentStepIndex(currentStepIndex - 1);
              }
            }}
            disabled={currentStepIndex === 0}
            className="flex-1"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Önceki
          </Button>
          
          <Button
            onClick={handleNext}
            className={`flex-1 bg-gradient-to-r ${currentProcedure?.color}`}
          >
            {currentStepIndex === currentProcedure.steps.length - 1 ? (
              <>Tamamla <CheckCircle className="w-4 h-4 ml-2" /></>
            ) : (
              <>Sonraki <ChevronRight className="w-4 h-4 ml-2" /></>
            )}
          </Button>
        </div>

        {/* Tip */}
        <div className="mt-6 text-center text-sm text-gray-500">
          💡 Komutu yüksek sesle ve net bir şekilde tekrarlayın
        </div>
      </div>
    </div>
    </CabinCrewLayout>
  );
}
